import { useCallback, useEffect, useState } from "react";
import { useCurrentAccount } from "./user.js";
import { FormattedMessage, useIntl } from "react-intl";
import { Anchor, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { captureEvent, isFeatureEnabled } from "../utils/posthog.js";
import { Event } from "../constants/events.js";
import { getAccountUserProductId, getCustomStyleProductId, getCustomTilesetProductId, getInputTokenProductId, getMapSessionProductId, getOutputTokenProductId, getProcessingProductId, getStorageProductId, getTileUsageProductId, TieredPriceProductId } from "@project/common";
import { endOfMonth, subMonths } from "date-fns";
import { TilesRequestsUsageCard } from "../routes/app/components/TilesRequestsUsageCard.js";
import { StorageUsageCard } from "../routes/app/components/StorageUsageCard.js";
import { MapLoadsUsageCard } from "../routes/app/components/MapLoadsUsageCard.js";
import { ProcessingUsageCard } from "../routes/app/components/ProcessingUsageCard.js";
import { AITokensUsageCard } from "../routes/app/components/AITokensUsageCard.js";
import { useProductIds } from "./subscription.js";
import { CustomStylesUsageCard } from "../routes/app/components/CustomStylesUsageCard.js";
import { CustomTilesetsUsageCard } from "../routes/app/components/CustomTilesetsUsageCard.js";
import { TeamSeatsUsageCard } from "../routes/app/components/TeamSeatsUsageCard.js";

const PAYMENT_MODAL_OPEN_KEY = "mapka:payment-modal-open";

const getIsPaymentModalOpen = () => {
  const isPaymentModalOpen = sessionStorage.getItem(PAYMENT_MODAL_OPEN_KEY);
  if (isPaymentModalOpen === "true") {
    return true;
  }
  if (isPaymentModalOpen === "false") {
    return false;
  }
  return null;
};

const setIsPaymentModalOpen = (value: boolean) => {
  sessionStorage.setItem(PAYMENT_MODAL_OPEN_KEY, value.toString());
};

export const useAboveFreeModal = () => {
  const intl = useIntl();
  const currentAccount = useCurrentAccount();
  const navigate = useNavigate();
  const open = getIsPaymentModalOpen();
  const [, rerender] = useState(0);
  const isPaymentsEnabled = isFeatureEnabled("enable-payments");
  const {
    isLoadingSubscriptions,
    tieredProductIds: productIds,
  } = useProductIds();

  useEffect(() => {
    if (typeof open === "boolean") {
      return;
    }
    if (typeof currentAccount === "undefined") {
      return;
    }
    setIsPaymentModalOpen(currentAccount.aboveFreeQuota.length > 0);
    rerender(r => r + 1);
  }, [open, currentAccount]);

  const handleClose = useCallback(() => {
    if (!currentAccount) return;
    captureEvent(Event.CancelPaymentRequiredModal);
    setIsPaymentModalOpen(false);
    rerender(r => r + 1);
  }, [currentAccount]);

  const handleNavigate = useCallback(() => {
    if (!currentAccount) return;
    captureEvent(Event.NavigateFromPaymentRequiredModal);
    setIsPaymentModalOpen(false);
    rerender(r => r + 1);
    void navigate({
      to: "/$accountName/admin/billing",
      params: { accountName: currentAccount.name },
      search: {
        tab: "payments",
      },
    });
  }, [navigate, currentAccount]);

  if (open !== true) {
    return null;
  }
  if (!currentAccount) {
    return null;
  }
  if (isLoadingSubscriptions) {
    return null;
  }
  if (!isPaymentsEnabled) {
    return null;
  }

  const prevMonth = subMonths(new Date(), 1);
  const from = endOfMonth(prevMonth);
  const to = endOfMonth(new Date());
  const interval = "month" as const;

  const {
    aboveFreeQuota,
  } = currentAccount;

  const cardProps = {
    from,
    to,
    interval,
    isLoadingSubscriptions: false,
  };

  const isAboveTileUsageLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredTileUsage);
  const isAboveStorageLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredStorage);
  const isAboveMapSessionLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredMapSession);
  const isAboveProcessingLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredProcessing);
  const isAboveInputTokenLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredInputToken);
  const isAboveOutputTokenLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredOutputToken);
  const isAboveCustomStyleLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredCustomStyle);
  const isAboveCustomTilesetLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredCustomTileset);
  const isAboveAccountUserLimit = aboveFreeQuota.includes(TieredPriceProductId.TieredAccountUser);
  const isAboveTokens = isAboveInputTokenLimit || isAboveOutputTokenLimit;

  return (
    <Modal
      opened={open}
      size="lg"
      onClose={handleClose}
      title={intl.formatMessage({
        defaultMessage: "Payment required",
        id: "notification.paymentRequired",
      })}
    >
      <Stack>
        <Text>
          <FormattedMessage
            id="text.paymentRequired"
            defaultMessage="You've reached one or more free usage limits and some actions are now unavailable. Add a payment method to continue. Usage above free limits is billed based on usage-based pricing. {pricingLink}"
            values={{
              pricingLink: (
                <Anchor underline="always" href="https://mapka.dev/pricing/" target="_blank" rel="noopener noreferrer">
                  View pricing
                </Anchor>
              ),
            }}
          />
        </Text>

        {isAboveTileUsageLimit && (
          <TilesRequestsUsageCard
            {...cardProps}
            productId={getTileUsageProductId(productIds)}
          />
        )}
        {isAboveStorageLimit && (
          <StorageUsageCard
            {...cardProps}
            productId={getStorageProductId(productIds)}
          />
        )}
        {isAboveMapSessionLimit && (
          <MapLoadsUsageCard
            {...cardProps}
            productId={getMapSessionProductId(productIds)}
          />
        )}
        {isAboveProcessingLimit && (
          <ProcessingUsageCard
            {...cardProps}
            productId={getProcessingProductId(productIds)}
          />
        )}
        {isAboveTokens && (
          <AITokensUsageCard
            {...cardProps}
            inputTokenProductId={getInputTokenProductId(productIds)}
            outputTokenProductId={getOutputTokenProductId(productIds)}
          />
        )}
        {isAboveCustomStyleLimit && (
          <CustomStylesUsageCard
            {...cardProps}
            productId={getCustomStyleProductId(productIds)}
          />
        )}
        {isAboveCustomTilesetLimit && (
          <CustomTilesetsUsageCard
            {...cardProps}
            productId={getCustomTilesetProductId(productIds)}
          />
        )}
        {isAboveAccountUserLimit && (
          <TeamSeatsUsageCard
            {...cardProps}
            productId={getAccountUserProductId(productIds)}
          />
        )}

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button
            variant="secondary"
            type="reset"
            onClick={handleClose}
          >
            <FormattedMessage id="button.continueWithoutPayment" defaultMessage="Continue without payment" />
          </Button>
          <Button
            variant="tertiary"
            onClick={handleNavigate}
          >
            <FormattedMessage id="button.payments" defaultMessage="Payments" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
